import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, XIcon, InstagramIcon } from 'lucide-react';
interface ShareCardProps {
  gameMode: string;
  players: string[];
  scores: Record<string, number[]>;
  onClose: () => void;
}
export function ShareCard({
  gameMode,
  players,
  scores,
  onClose
}: ShareCardProps) {
  // Reuse winner logic from WinnerScreen
  const getWinnerData = () => {
    if (gameMode === 'challenge') {
      const totals = players.map(p => ({
        name: p,
        score: scores[p].reduce((a, b) => a + b, 0)
      })).sort((a, b) => b.score - a.score);
      return {
        name: totals[0].name,
        score: totals[0].score,
        label: 'Points'
      };
    } else if (gameMode === 'match' && players.length === 2) {
      let p1Wins = 0,
        p2Wins = 0;
      for (let i = 0; i < scores[players[0]].length; i++) {
        if (scores[players[0]][i] > 0 && scores[players[1]][i] > 0) {
          if (scores[players[0]][i] < scores[players[1]][i]) p1Wins++;else if (scores[players[1]][i] < scores[players[0]][i]) p2Wins++;
        }
      }
      let statusText = 'Draw',
        winnerName = 'Draw';
      if (p1Wins > p2Wins) {
        statusText = `${p1Wins - p2Wins} UP`;
        winnerName = players[0];
      } else if (p2Wins > p1Wins) {
        statusText = `${p2Wins - p1Wins} UP`;
        winnerName = players[1];
      }
      return {
        name: winnerName,
        score: statusText,
        label: 'Match Result'
      };
    } else if (gameMode === 'bestball' && players.length === 4) {
      const t1Score = scores[players[0]].reduce((a, b) => a + b, 0) + scores[players[1]].reduce((a, b) => a + b, 0);
      const t2Score = scores[players[2]].reduce((a, b) => a + b, 0) + scores[players[3]].reduce((a, b) => a + b, 0);
      const t1Won = t1Score < t2Score;
      return {
        name: t1Won ? 'Team 1' : 'Team 2',
        score: t1Won ? t1Score : t2Score,
        label: 'Team Score'
      };
    } else {
      const totals = players.map(p => ({
        name: p,
        score: scores[p].reduce((a, b) => a + b, 0)
      })).sort((a, b) => a.score - b.score);
      return {
        name: totals[0].name,
        score: totals[0].score,
        label: 'Strokes'
      };
    }
  };
  const winner = getWinnerData();
  return <div className="flex flex-col h-full bg-brand-charcoal relative" data-id="element-310">
      <div className="flex items-center justify-between p-6 pb-2" data-id="element-311">
        <h2 className="text-xl font-heading text-brand-cream-light" data-id="element-312">
          Share Result
        </h2>
        <button onClick={onClose} className="p-2 bg-brand-cream/10 rounded-full text-brand-cream-light" data-id="element-313">
          <XIcon className="w-5 h-5" data-id="element-314" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6" data-id="element-315">
        {/* The Card to be "shared" */}
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="w-full aspect-[4/5] bg-brand-green-deep rounded-3xl border border-brand-gold/30 shadow-2xl relative overflow-hidden flex flex-col" data-id="element-316">
          {/* Card Background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.3)_0%,transparent_70%)]" data-id="element-317" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPC9zdmc+')] opacity-10 mix-blend-overlay" data-id="element-318" />

          <div className="relative z-10 flex-1 flex flex-col p-8" data-id="element-319">
            <div className="text-center mb-auto" data-id="element-320">
              <span className="font-heading text-xl text-brand-cream-light tracking-wide block" data-id="element-321">
                PuttLab
              </span>
              <span className="font-body text-[8px] tracking-[0.3em] text-brand-gold uppercase" data-id="element-322">
                The Sunset Green
              </span>
            </div>

            <div className="text-center my-auto" data-id="element-323">
              <span className="text-brand-gold text-xs font-bold uppercase tracking-[0.2em] mb-2 block" data-id="element-324">
                {gameMode === 'challenge' ? 'Challenge Champion' : 'Course Record'}
              </span>
              <h3 className="text-4xl font-heading text-brand-cream-light mb-1" data-id="element-325">
                {winner.name}
              </h3>
              <div className={`font-heading text-brand-gold leading-none my-4 ${gameMode === 'match' ? 'text-5xl' : 'text-8xl'}`} data-id="element-326">
                {winner.score}
              </div>
              <span className="text-brand-cream/60 text-sm font-body uppercase tracking-widest" data-id="element-327">
                {winner.label}
              </span>
            </div>

            <div className="mt-auto pt-8 border-t border-brand-cream/10 flex justify-between items-end" data-id="element-328">
              <div className="text-left" data-id="element-329">
                <span className="text-[10px] text-brand-cream/40 uppercase tracking-widest block mb-1" data-id="element-330">
                  Date
                </span>
                <span className="text-xs text-brand-cream-light font-body" data-id="element-331">
                  {new Date().toLocaleDateString()}
                </span>
              </div>
              <div className="w-12 h-12 bg-brand-cream-light rounded-lg flex items-center justify-center p-1" data-id="element-332">
                {/* Fake QR code pattern */}
                <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iMiIgaGVpZ2h0PSIyIiBmaWxsPSIjMEYyNDE5Ii8+Cjwvc3ZnPg==')] opacity-80" data-id="element-333" />
              </div>
            </div>
          </div>
        </motion.div>

        <p className="text-brand-cream/50 text-sm font-body text-center mt-8" data-id="element-334">
          Save this image to your camera roll or share directly to your story.
        </p>
      </div>

      <div className="p-6 grid grid-cols-2 gap-4" data-id="element-335">
        <button className="flex items-center justify-center space-x-2 bg-brand-cream/10 text-brand-cream-light font-body py-4 rounded-xl hover:bg-brand-cream/20 transition-colors" data-id="element-336">
          <DownloadIcon className="w-5 h-5" data-id="element-337" />
          <span data-id="element-338">Save Image</span>
        </button>
        <button className="flex items-center justify-center space-x-2 bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400 text-white font-body py-4 rounded-xl hover:opacity-90 transition-opacity shadow-lg" data-id="element-339">
          <InstagramIcon className="w-5 h-5" data-id="element-340" />
          <span data-id="element-341">Story</span>
        </button>
      </div>
    </div>;
}